<template>
  <div class="breathing-guide" :class="{ 'breathing-guide--disabled': disabled }">
    <div class="breathing-guide__body">
      <div class="breathing-bar">
        <div class="breathing-visual">
          <!-- Cercle principal -->
          <div class="breathing-circle-container">
            <!-- Anneau de progression de la phase -->
            <svg class="breathing-progress" viewBox="0 0 120 120" aria-hidden="true">
              <circle class="breathing-progress__background" cx="60" cy="60" r="56" />
              <circle class="breathing-progress__value" cx="60" cy="60" r="56" :style="progressCircleStyle" />
            </svg>
            <!-- Cercle animé -->
            <div :key="breathAnimationVersion" class="breathing-circle" :class="breathingCircleClass"
              :style="circleShellStyle">
              <div class="breathing-circle__fill-track">
                <div class="breathing-circle__fill" :style="fillStyle"></div>
              </div>

              <button type="button" class="breathing-circle__sound"
                :class="{ 'breathing-circle__sound--active': metronomeEnabled }" :aria-label="metronomeEnabled
                  ? 'Désactiver le guide audio'
                  : 'Activer le guide audio'
                  " :title="metronomeEnabled
                    ? 'Désactiver le guide audio'
                    : 'Activer le guide audio'
                    " @click.stop="toggleBreathingSound">
                <i :class="metronomeEnabled
                  ? 'fas fa-volume-up'
                  : 'fas fa-volume-mute'
                  "></i>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div v-if="openModal" ref="modalRoot" class="breathing-modal__overlay" @click.self="openModal = false">
      <div class="breathing-modal" @click.stop>
        <header class="breathing-modal__header">
          <div>
            <h3 class="breathing-modal__title">Paramètres respiratoires</h3>
            <p class="breathing-modal__subtitle">
              Ajustez votre rythme de respiration
            </p>
          </div>
          <button class="icon-btn" type="button" aria-label="Fermer" @click="openModal = false">
            <i class="fas fa-times"></i>
          </button>
        </header>
        <div class="breathing-modal__body">
          <section class="breathing-modal__section">
            <label class="field-label">Cycles par minute</label>
            <select v-model="cpm" @change="onPresetChange" class="field-input">
              <option value="5">5 CPM - Très lent</option>
              <option value="6">6 CPM - Recommandé</option>
              <option value="7">7 CPM - Modéré</option>
              <option value="8">8 CPM - Dynamique</option>
              <option value="10">10 CPM - Rapide</option>
              <option value="12">12 CPM - Très rapide</option>
            </select>
          </section>
          <section class="breathing-modal__section">
            <label class="field-label">Profil de respiration</label>
            <select v-model="profile" @change="onPresetChange" class="field-input">
              <option value="basique">Basique - Simple et efficace</option>
              <option value="profonde">Profonde - Relaxation intense</option>
              <option value="tonique">Tonique - Énergisant</option>
            </select>
          </section>
          <section class="breathing-modal__section">
            <div class="timing-grid">
              <div class="timing-card">
                <span>Inspiration</span>
                <div class="timing-controls">
                  <button class="icon-btn icon-btn--small" :disabled="inspiration <= 0.1"
                    @click="changeInspiration(-0.1)">
                    <i class="fas fa-minus"></i>
                  </button>
                  <strong>{{ inspiration }} s</strong>
                  <button class="icon-btn icon-btn--small" @click="changeInspiration(0.1)">
                    <i class="fas fa-plus"></i>
                  </button>
                </div>
              </div>
              <div class="timing-card">
                <span>Expiration</span>
                <div class="timing-controls">
                  <button class="icon-btn icon-btn--small" :disabled="expiration <= 0.1"
                    @click="changeExpiration(-0.1)">
                    <i class="fas fa-minus"></i>
                  </button>
                  <strong>{{ expiration }} s</strong>
                  <button class="icon-btn icon-btn--small" @click="changeExpiration(0.1)">
                    <i class="fas fa-plus"></i>
                  </button>
                </div>
              </div>
              <div class="timing-card">
                <span>Pause inspiration</span>
                <div class="timing-controls">
                  <button class="icon-btn icon-btn--small" :disabled="pauseInspiration <= 0"
                    @click="changePauseInspiration(-0.1)">
                    <i class="fas fa-minus"></i>
                  </button>
                  <strong>{{ pauseInspiration }} s</strong>
                  <button class="icon-btn icon-btn--small" @click="changePauseInspiration(0.1)">
                    <i class="fas fa-plus"></i>
                  </button>
                </div>
              </div>
              <div class="timing-card">
                <span>Pause expiration</span>
                <div class="timing-controls">
                  <button class="icon-btn icon-btn--small" :disabled="pauseExpiration <= 0"
                    @click="changePauseExpiration(-0.1)">
                    <i class="fas fa-minus"></i>
                  </button>
                  <strong>{{ pauseExpiration }} s</strong>
                  <button class="icon-btn icon-btn--small" @click="changePauseExpiration(0.1)">
                    <i class="fas fa-plus"></i>
                  </button>
                </div>
              </div>
            </div>
          </section>
          <section class="breathing-modal__section">
            <label class="checkbox-row">
              <input type="checkbox" v-model="metronomeEnabled" @change="onAudioToggle" />
              <span>Activer le guide audio</span>
            </label>
            <div v-if="metronomeEnabled" ref="soundSelector">
              <label class="field-label">Type de son</label>
              <select v-model="soundType" class="field-input">
                <option value="chime">Carillon doux</option>
                <option value="bowl">Bol tibétain</option>
                <option value="breath">Souffle</option>
                <option value="drop">Goutte d’eau</option>
                <option value="wood">Bois doux</option>
                <option value="wave">Vague</option>
                <option value="pulse">Pulse cardiaque</option>
                <option value="softBeep">Bip doux</option>
              </select>
            </div>
          </section>
        </div>
        <footer class="breathing-modal__footer">
          <button class="ghost-btn" type="button" @click="recupDataCPM">
            Récupérer mes paramètres
          </button>
          <button class="primary-btn" type="button" @click="SaveDataCPM">
            Enregistrer mes paramètres
          </button>
        </footer>
      </div>
    </div>
  </div>
</template>
<script>
import Parse from "parse";
export default {
  name: "BreathingGuide",
  emits: ["update-breath-settings", "breath-phase-change"],
  props: {
    idUserForSave: String,
    idPatientTherapeute: String,
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      soundType: "wave",
      openModal: false,
      cpm: "6",
      profile: "basique",
      inspiration: 4.5,
      expiration: 5.5,
      pauseInspiration: 0,
      pauseExpiration: 0,
      currentPhase: null,
      UtilisateurCPM: null,
      metronomeEnabled: false,
      audioCtx: null,
      activeAudioNodes: [],
      cycleTimer: null,
      cycleToken: 0,
      breathAnimationVersion: 0,
      phaseRemaining: 0,
      phaseDuration: 0,
      phaseStartedAt: null,
      // Horloge haute précision du cycle respiratoire courant.
      currentCycleStartedAtPerf: null,
      phaseCountdownTimer: null,
      phaseProgressValue: 0,
      breathFillLevel: 0,
      // Valeur commune du cercle et du filet extérieur, sans inertie
      breathRingLevel: 0,
    };
  },
  computed: {
    animationDuration() {
      const total =
        Number(this.inspiration) +
        Number(this.pauseInspiration) +
        Number(this.expiration) +
        Number(this.pauseExpiration);
      return Math.max(0.2, total);
    },
    easedPhaseProgress() {
      const t = this.phaseProgress;
      return t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2;
    },
    circleShellStyle() {
      const fill = Math.min(1, Math.max(0, this.breathRingLevel));

      return {
        boxShadow: `0 0 ${10 + fill * 14}px rgba(75, 220, 214, ${0.10 + fill * 0.18})`,
      };
    },

    fillStyle() {
      const fill = Math.min(1, Math.max(0, this.breathRingLevel));

      return {
        height: `${fill * 100}%`,
        opacity: 0.20 + fill * 0.75,
      };
    },
    phaseDisplayLabel() {
      switch (this.currentPhase) {
        case "Inspirez":
          return "INSPIREZ";
        case "Expirez":
          return "EXPIREZ";
        case "Tenez":
          return "MAINTENEZ";
        case "Pause":
          return "RELÂCHEZ";
        default:
          return "PRÉPAREZ-VOUS";
      }
    },
    breathingDirectionIcon() {
      switch (this.currentPhase) {
        case "Inspirez":
          return "fas fa-arrow-up";
        case "Expirez":
          return "fas fa-arrow-down";
        case "Tenez":
          return "fas fa-pause";
        case "Pause":
          return "fas fa-pause";
        default:
          return "fas fa-circle";
      }
    },
    breathingDirectionClass() {
      return {
        "breathing-direction--inspiration": this.currentPhase === "Inspirez",
        "breathing-direction--expiration": this.currentPhase === "Expirez",
        "breathing-direction--pause":
          this.currentPhase === "Tenez" || this.currentPhase === "Pause",
      };
    },
    breathingCircleClass() {
      return {
        "breathing-circle--inspiration": this.currentPhase === "Inspirez",
        "breathing-circle--expiration": this.currentPhase === "Expirez",
        "breathing-circle--pause":
          this.currentPhase === "Tenez" || this.currentPhase === "Pause",
      };
    },
    phaseProgress() {
      return this.phaseProgressValue;
    },
    progressCircleStyle() {
      const radius = 56;
      const circumference = 2 * Math.PI * radius;
      /*
       * 0 = filet vide
       * 1 = filet complètement rempli
       */
      const progress = Math.min(1, Math.max(0, this.breathRingLevel));
      return {
        strokeDasharray: `${circumference} ${circumference}`,
        strokeDashoffset: circumference * (1 - progress),
      };
    },
  },
  methods: {
    async toggleBreathingSound() {
      this.metronomeEnabled = !this.metronomeEnabled;
      await this.onAudioToggle();
    },
    openSettings() {
      this.openModal = true;
      this.syncPortalInteraction();
      this.$nextTick(() => this.ensurePortalMount());
    },
    syncPortalInteraction() {
      const portal = document.getElementById("monitoring-modal-root");
      if (portal) {
        portal.style.pointerEvents = this.openModal ? "auto" : "none";
      }
    },
    ensurePortalMount() {
      try {
        const portal = document.getElementById("monitoring-modal-root");
        if (
          portal &&
          this.$refs.modalRoot &&
          this.$refs.modalRoot.parentNode !== portal
        ) {
          portal.appendChild(this.$refs.modalRoot);
        }
      } catch (e) {
        // ignore
      }
    },
    emitBreathSettings() {
      this.$emit("update-breath-settings", {
        cpm: Number(this.cpm),
        inspiration: Number(this.inspiration),
        expiration: Number(this.expiration),
        pauseInspiration: Number(this.pauseInspiration),
        pauseExpiration: Number(this.pauseExpiration),
        profile: this.profile,
      });
    },
    normalizeNumber(value, min = 0) {
      return Math.max(min, parseFloat(Number(value).toFixed(1)));
    },
    updateCycle(settings = null) {
      if (settings) {
        this.inspiration = this.normalizeNumber(
          settings.inspi !== undefined && settings.inspi !== null
            ? settings.inspi
            : this.inspiration,
          0.1,
        );
        this.pauseInspiration = this.normalizeNumber(
          settings.pauseInspi !== undefined && settings.pauseInspi !== null
            ? settings.pauseInspi
            : this.pauseInspiration,
          0,
        );
        this.expiration = this.normalizeNumber(
          settings.expi !== undefined && settings.expi !== null
            ? settings.expi
            : this.expiration,
          0.1,
        );
        this.pauseExpiration = this.normalizeNumber(
          settings.pauseExpi !== undefined && settings.pauseExpi !== null
            ? settings.pauseExpi
            : this.pauseExpiration,
          0,
        );
      } else {
        this.updateBreathingPattern();
      }
      this.restartBreathingSystem();
    },
    restartBreathingSystem() {
      this.emitBreathSettings();
      this.stopCycleTimer();
      this.stopActiveAudio();
      this.currentPhase = null;
      this.currentCycleStartedAtPerf = null;
      this.cycleToken += 1;
      const token = this.cycleToken;
      this.updateAnimation();
      this.$nextTick(() => {
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            if (token !== this.cycleToken) return;
            this.startPhaseScheduler(token);
          });
        });
      });
    },
    onPresetChange() {
      this.updateBreathingPattern();
      this.restartBreathingSystem();
    },
    changeInspiration(value) {
      this.inspiration = this.normalizeNumber(this.inspiration + value, 0.1);
      this.restartBreathingSystem();
    },
    changeExpiration(value) {
      this.expiration = this.normalizeNumber(this.expiration + value, 0.1);
      this.restartBreathingSystem();
    },
    changePauseInspiration(value) {
      this.pauseInspiration = this.normalizeNumber(
        this.pauseInspiration + value,
        0,
      );
      this.restartBreathingSystem();
    },
    changePauseExpiration(value) {
      this.pauseExpiration = this.normalizeNumber(
        this.pauseExpiration + value,
        0,
      );
      this.restartBreathingSystem();
    },
    updateBreathingPattern() {
      const cpm = Number(this.cpm);
      const patterns = {
        5: {
          basique: [5.4, 6.6],
          tonique: [6, 6],
          profonde: [4.8, 7.2],
        },
        6: {
          basique: [4.5, 5.5],
          tonique: [5, 5],
          profonde: [4, 6],
        },
        7: {
          basique: [4.29, 4.29],
          tonique: [3.86, 4.71],
          profonde: [3.43, 5.14],
        },
        8: {
          basique: [3.38, 4.13],
          tonique: [3.75, 3.75],
          profonde: [3, 4.5],
        },
        10: {
          basique: [2.7, 3.3],
          tonique: [3, 3],
          profonde: [2.4, 3.6],
        },
        12: {
          basique: [2, 3],
          tonique: [2.5, 2.5],
          profonde: [2, 3],
        },
      };
      const selected = patterns[cpm] && patterns[cpm][this.profile];
      if (selected) {
        this.inspiration = selected[0];
        this.expiration = selected[1];
      }
    },
    updateAnimation() {
      const oldStyle = document.getElementById("breathing-animation-style");
      if (oldStyle) {
        oldStyle.remove();
      }
      const i = Number(this.inspiration);
      const pi = Number(this.pauseInspiration);
      const e = Number(this.expiration);
      const total = this.animationDuration;
      const pInspiEnd = (i / total) * 100;
      const pPauseInspiEnd = ((i + pi) / total) * 100;
      const pExpiEnd = ((i + pi + e) / total) * 100;
      const keyframes = `
    @keyframes breathing {
      0% {
        transform: scale(0.4);
      }
      ${pInspiEnd}% {
        transform: scale(1);
      }
      ${pPauseInspiEnd}% {
        transform: scale(1);
      }
      ${pExpiEnd}% {
        transform: scale(0.4);
      }
      100% {
        transform: scale(0.4);
      }
    }
  `;
      const style = document.createElement("style");
      style.id = "breathing-animation-style";
      style.textContent = keyframes;
      document.head.appendChild(style);
      this.breathAnimationVersion += 1;
    },
    getPhaseSequence() {
      return [
        {
          name: "Inspirez",
          audioType: "inspi",
          duration: Number(this.inspiration),
        },
        {
          name: "Tenez",
          audioType: null,
          duration: Number(this.pauseInspiration),
        },
        {
          name: "Expirez",
          audioType: "expi",
          duration: Number(this.expiration),
        },
        {
          name: "Pause",
          audioType: null,
          duration: Number(this.pauseExpiration),
        },
      ].filter((phase) => phase.duration > 0);
    },
    restartAudioCycle() {
      this.stopCycleTimer();
      this.stopActiveAudio();
      this.currentPhase = null;
      this.currentCycleStartedAtPerf = null;
      this.cycleToken += 1;
      const token = this.cycleToken;
      this.$nextTick(() => {
        this.startPhaseScheduler(token);
      });
    },
    stopCycleTimer() {
      if (this.cycleTimer) {
        clearTimeout(this.cycleTimer);
        this.cycleTimer = null;
      }
      this.stopPhaseCountdown();
      this.phaseRemaining = 0;
      this.phaseDuration = 0;
    },
    startPhaseCountdown(duration, token, startedAt = performance.now()) {
      this.stopPhaseCountdown();
      this.phaseDuration = Number(duration);
      this.phaseRemaining = Number(duration);
      this.phaseProgressValue = 0;
      this.phaseStartedAt = startedAt;

      const updateCountdown = () => {
        if (token !== this.cycleToken) return;
        const elapsed = Math.max(0, (performance.now() - this.phaseStartedAt) / 1000);
        const progress = Math.min(1, elapsed / this.phaseDuration);
        // Progression répartie sur toute la phase, sans inertie.
        // Vitesse nulle aux extrémités, variable et plafonnée à 1,25
        // (contre PI/2 pour la courbe précédente) : moins de précipitation au milieu.
        // Intensité du ralentissement au début et à la fin.
        // 2 = modéré ; 2.5 = marqué ; 3 = très marqué.
        const douceur = 3;

        const easedProgress =
          progress < 0.5
            ? Math.pow(2 * progress, douceur) / 2
            : 1 - Math.pow(2 * (1 - progress), douceur) / 2;
        this.phaseProgressValue = progress;
        if (this.currentPhase === "Inspirez") {
          this.breathFillLevel = easedProgress;
        } else if (this.currentPhase === "Expirez") {
          this.breathFillLevel = 1 - easedProgress;
        } else if (this.currentPhase === "Tenez") {
          this.breathFillLevel = 1;
        } else {
          this.breathFillLevel = 0;
        }
        // Le cercle et l'anneau suivent exactement la même valeur.
        this.breathRingLevel = this.breathFillLevel;
        this.phaseRemaining = Math.max(0, this.phaseDuration - elapsed);
        this.phaseCountdownTimer = progress < 1
          ? requestAnimationFrame(updateCountdown)
          : null;
      };
      // Appliquer immédiatement la taille de départ, y compris pendant les pauses.
      updateCountdown();
    },
    stopPhaseCountdown() {
      if (this.phaseCountdownTimer) {
        cancelAnimationFrame(this.phaseCountdownTimer);
        this.phaseCountdownTimer = null;
      }
      this.phaseStartedAt = null;
    },
    startPhaseScheduler(token) {
      if (token !== this.cycleToken) return;
      const phases = this.getPhaseSequence();
      if (!phases.length) return;
      let phaseIndex = 0;
      const runPhase = () => {
        if (token !== this.cycleToken) return;
        const phase = phases[phaseIndex];
        // Instant réel de début de phase, sur la même horloge haute précision
        // que celle utilisée pour l'animation du guide.
        const phaseStartedAtPerf = performance.now();
        this.currentPhase = phase.name;
        // Une nouvelle inspiration marque le début réel d'un nouveau cycle.
        if (phase.name === "Inspirez") {
          this.currentCycleStartedAtPerf = phaseStartedAtPerf;
        }
        /*
         * Position théorique de la phase dans le cycle.
         * Le parent combine cet offset avec phaseStartedAtPerf :
         * le tachogramme se recale donc sur le début réellement affiché
         * de CHAQUE phase et corrige automatiquement un éventuel retard
         * de setTimeout.
         */
        const phaseOffsets = {
          Inspirez: 0,
          Tenez: Number(this.inspiration),
          Expirez: Number(this.inspiration) + Number(this.pauseInspiration),
          Pause:
            Number(this.inspiration) +
            Number(this.pauseInspiration) +
            Number(this.expiration),
        };
        this.$emit("breath-phase-change", {
          phase: phase.name,
          cycleOffset: phaseOffsets[phase.name] || 0,
          // Horodatages haute précision pour la synchronisation du graphe.
          phaseStartedAtPerf,
          cycleStartedAtPerf: this.currentCycleStartedAtPerf,
          cpm: Number(this.cpm),
          inspiration: Number(this.inspiration),
          pauseInspiration: Number(this.pauseInspiration),
          expiration: Number(this.expiration),
          pauseExpiration: Number(this.pauseExpiration),
          profile: this.profile,
        });
        // Démarre le compteur visuel
        this.startPhaseCountdown(
          phase.duration,
          token,
          phaseStartedAtPerf,
        );
        // Audio
        if (this.metronomeEnabled && phase.audioType) {
          this.playBreathSound(phase.audioType, phase.duration);
        }
        const delay = Math.max(50, phase.duration * 1000);
        this.cycleTimer = setTimeout(() => {
          phaseIndex = (phaseIndex + 1) % phases.length;
          runPhase();
        }, delay);
      };
      runPhase();
    },
    async onAudioToggle() {
      if (this.metronomeEnabled) {
        await this.unlockAudio();
        this.$nextTick(() => {
          setTimeout(() => {
            const el = this.$refs.soundSelector;
            if (el) {
              el.scrollIntoView({
                behavior: "smooth",
                block: "center",
              });
            }
          }, 100);
        });
      } else {
        this.stopActiveAudio();
      }
      this.restartAudioCycle();
    },
    async unlockAudio() {
      try {
        const AudioContext = window.AudioContext || window.webkitAudioContext;
        if (!AudioContext) {
          console.warn("Web Audio API non supportée.");
          return;
        }
        if (!this.audioCtx) {
          this.audioCtx = new AudioContext();
        }
        if (this.audioCtx.state === "suspended") {
          await this.audioCtx.resume();
        }
        const ctx = this.audioCtx;
        const oscillator = ctx.createOscillator();
        const gain = ctx.createGain();
        gain.gain.value = 0.0001;
        oscillator.connect(gain);
        gain.connect(ctx.destination);
        oscillator.start();
        oscillator.stop(ctx.currentTime + 0.05);
      } catch (error) {
        console.warn("Audio non disponible :", error);
      }
    },
    playBreathSound(type, duration) {
      try {
        const AudioContext = window.AudioContext || window.webkitAudioContext;
        if (!AudioContext) return;
        if (!this.audioCtx) {
          this.audioCtx = new AudioContext();
        }
        const ctx = this.audioCtx;
        if (ctx.state === "suspended") ctx.resume();
        const now = ctx.currentTime;
        const phaseDuration = Math.max(0.8, Number(duration));
        const isInspi = type === "inspi";
        const soundDurations = {
          wave: phaseDuration,
          breath: phaseDuration,
          chime: isInspi ? 1.2 : 1.4,
          bowl: isInspi ? 2.2 : 2.8,
          drop: isInspi ? 0.2 : 0.3,
          wood: isInspi ? 0.4 : 0.6,
          pulse: isInspi ? 0.4 : 0.6,
          softBeep: isInspi ? 0.1 : 0.2,
        };
        const realDuration = Math.min(
          phaseDuration,
          soundDurations[this.soundType] || phaseDuration,
        );
        const registerNodeGroup = (nodeGroup) => {
          this.activeAudioNodes.push(nodeGroup);
          const source = nodeGroup.source || nodeGroup.oscillator;
          if (source) {
            source.onended = () => {
              this.disconnectAudioNodeGroup(nodeGroup);
              this.activeAudioNodes = this.activeAudioNodes.filter(
                (node) => node !== nodeGroup,
              );
            };
          }
        };
        const envelope = (
          gain,
          volume,
          attackRatio = 0.25,
          releaseRatio = 0.25,
        ) => {
          const attack = Math.min(1.2, realDuration * attackRatio);
          const release = Math.min(1.2, realDuration * releaseRatio);
          const sustainEnd = Math.max(
            now + attack,
            now + realDuration - release,
          );
          gain.gain.setValueAtTime(0.0001, now);
          gain.gain.linearRampToValueAtTime(volume, now + attack);
          gain.gain.setValueAtTime(volume, sustainEnd);
          gain.gain.linearRampToValueAtTime(0.0001, now + realDuration);
        };
        const createTone = ({
          frequencyStart,
          frequencyEnd,
          volume = 0.035,
          oscillatorType = "sine",
          filterFrequency = 900,
          attackRatio = 0.1,
          releaseRatio = 0.1,
        }) => {
          const oscillator = ctx.createOscillator();
          const gain = ctx.createGain();
          const filter = ctx.createBiquadFilter();
          oscillator.type = oscillatorType;
          filter.type = "lowpass";
          filter.frequency.setValueAtTime(filterFrequency, now);
          oscillator.frequency.setValueAtTime(frequencyStart, now);
          if (frequencyEnd) {
            oscillator.frequency.linearRampToValueAtTime(
              frequencyEnd,
              now + realDuration,
            );
          }
          envelope(gain, volume, attackRatio, releaseRatio);
          oscillator.connect(filter);
          filter.connect(gain);
          gain.connect(ctx.destination);
          oscillator.start(now);
          oscillator.stop(now + realDuration + 0.08);
          registerNodeGroup({
            source: oscillator,
            oscillator,
            filter,
            gain,
          });
        };
        const createNoise = ({
          volume = 0.028,
          filterFrequencyStart = 700,
          filterFrequencyEnd = null,
          filterType = "lowpass",
          attackRatio = 0.3,
          releaseRatio = 0.35,
        }) => {
          const bufferSize = Math.floor(ctx.sampleRate * realDuration);
          const buffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate);
          const data = buffer.getChannelData(0);
          for (let i = 0; i < bufferSize; i++) {
            data[i] = Math.random() * 2 - 1;
          }
          const source = ctx.createBufferSource();
          const filter = ctx.createBiquadFilter();
          const gain = ctx.createGain();
          source.buffer = buffer;
          filter.type = filterType;
          filter.frequency.setValueAtTime(filterFrequencyStart, now);
          if (filterFrequencyEnd) {
            filter.frequency.linearRampToValueAtTime(
              filterFrequencyEnd,
              now + realDuration,
            );
          }
          envelope(gain, volume, attackRatio, releaseRatio);
          source.connect(filter);
          filter.connect(gain);
          gain.connect(ctx.destination);
          source.start(now);
          source.stop(now + realDuration + 0.05);
          registerNodeGroup({
            source,
            filter,
            gain,
          });
        };
        switch (this.soundType) {
          case "chime":
            createTone({
              frequencyStart: isInspi ? 520 : 440,
              frequencyEnd: isInspi ? 660 : 330,
              volume: 0.026,
              filterFrequency: 1400,
              attackRatio: 0.18,
              releaseRatio: 0.45,
            });
            break;
          case "bowl": {
            const baseFreq = isInspi ? 174 : 146;
            const osc = ctx.createOscillator();
            const gain = ctx.createGain();
            const filter = ctx.createBiquadFilter();
            osc.type = "sine";
            osc.frequency.setValueAtTime(baseFreq, now);
            osc.frequency.linearRampToValueAtTime(
              baseFreq * (isInspi ? 1.12 : 0.9),
              now + realDuration,
            );
            filter.type = "lowpass";
            filter.frequency.setValueAtTime(900, now);
            filter.Q.value = 10;
            const attack = 0.08;
            const release = Math.min(3.5, realDuration * 0.8);
            gain.gain.setValueAtTime(0.0001, now);
            gain.gain.exponentialRampToValueAtTime(0.05, now + attack);
            gain.gain.setValueAtTime(0.05, now + attack);
            gain.gain.exponentialRampToValueAtTime(0.0001, now + release);
            const delay = ctx.createDelay();
            delay.delayTime.value = 0.08;
            const feedback = ctx.createGain();
            feedback.gain.value = 0.45;
            delay.connect(feedback);
            feedback.connect(delay);
            osc.connect(filter);
            filter.connect(gain);
            gain.connect(ctx.destination);
            filter.connect(delay);
            delay.connect(gain);
            osc.start(now);
            osc.stop(now + release + 0.5);
            registerNodeGroup({
              source: osc,
              oscillator: osc,
              filter,
              gain,
              delay,
              feedback,
            });
            break;
          }
          case "breath": {
            createNoise({
              volume: 0.032,
              filterType: "bandpass",
              filterFrequencyStart: isInspi ? 500 : 350,
              filterFrequencyEnd: isInspi ? 900 : 250,
              attackRatio: 0.25,
              releaseRatio: 0.3,
            });
            break;
          }
          case "drop": {
            const impactFreq = isInspi ? 620 : 480;
            const bodyFreq = isInspi ? 220 : 170;
            const impactOsc = ctx.createOscillator();
            const impactGain = ctx.createGain();
            const impactFilter = ctx.createBiquadFilter();
            impactOsc.type = "triangle";
            impactOsc.frequency.setValueAtTime(impactFreq, now);
            impactOsc.frequency.exponentialRampToValueAtTime(
              impactFreq * 0.45,
              now + 0.08,
            );
            impactFilter.type = "bandpass";
            impactFilter.frequency.setValueAtTime(900, now);
            impactFilter.Q.value = 7;
            impactGain.gain.setValueAtTime(0.0001, now);
            impactGain.gain.exponentialRampToValueAtTime(0.055, now + 0.008);
            impactGain.gain.exponentialRampToValueAtTime(0.0001, now + 0.12);
            impactOsc.connect(impactFilter);
            impactFilter.connect(impactGain);
            impactGain.connect(ctx.destination);
            const bodyOsc = ctx.createOscillator();
            const bodyGain = ctx.createGain();
            const bodyFilter = ctx.createBiquadFilter();
            bodyOsc.type = "sine";
            bodyOsc.frequency.setValueAtTime(bodyFreq, now + 0.02);
            bodyOsc.frequency.exponentialRampToValueAtTime(
              bodyFreq * 0.65,
              now + 0.28,
            );
            bodyFilter.type = "lowpass";
            bodyFilter.frequency.setValueAtTime(500, now);
            bodyFilter.Q.value = 1.2;
            bodyGain.gain.setValueAtTime(0.0001, now + 0.02);
            bodyGain.gain.exponentialRampToValueAtTime(0.035, now + 0.04);
            bodyGain.gain.exponentialRampToValueAtTime(0.0001, now + 0.42);
            bodyOsc.connect(bodyFilter);
            bodyFilter.connect(bodyGain);
            bodyGain.connect(ctx.destination);
            const delay = ctx.createDelay();
            delay.delayTime.value = 0.075;
            const feedback = ctx.createGain();
            feedback.gain.value = 0.18;
            impactFilter.connect(delay);
            bodyFilter.connect(delay);
            delay.connect(feedback);
            feedback.connect(delay);
            delay.connect(bodyGain);
            impactOsc.start(now);
            impactOsc.stop(now + 0.14);
            bodyOsc.start(now + 0.02);
            bodyOsc.stop(now + 0.48);
            registerNodeGroup({
              source: impactOsc,
              oscillator: impactOsc,
              gain: impactGain,
              filter: impactFilter,
              delay,
              feedback,
            });
            registerNodeGroup({
              source: bodyOsc,
              oscillator: bodyOsc,
              gain: bodyGain,
              filter: bodyFilter,
            });
            break;
          }
          case "wood": {
            createTone({
              frequencyStart: isInspi ? 260 : 210,
              frequencyEnd: isInspi ? 300 : 180,
              volume: 0.025,
              oscillatorType: "triangle",
              filterFrequency: 650,
              attackRatio: 0.12,
              releaseRatio: 0.55,
            });
            break;
          }
          case "wave": {
            createNoise({
              volume: 0.035,
              filterType: "lowpass",
              filterFrequencyStart: isInspi ? 400 : 600,
              filterFrequencyEnd: isInspi ? 900 : 300,
              attackRatio: 0.3,
              releaseRatio: 0.3,
            });
            break;
          }
          case "pulse": {
            createTone({
              frequencyStart: isInspi ? 120 : 95,
              frequencyEnd: isInspi ? 135 : 80,
              volume: 0.03,
              filterFrequency: 450,
              attackRatio: 0.25,
              releaseRatio: 0.5,
            });
            break;
          }
          case "softBeep": {
            createTone({
              frequencyStart: isInspi ? 390 : 320,
              frequencyEnd: isInspi ? 460 : 260,
              volume: 0.022,
              filterFrequency: 900,
              attackRatio: 0.18,
              releaseRatio: 0.45,
            });
            break;
          }
          default: {
            createNoise({
              volume: 0.032,
              filterFrequencyStart: isInspi ? 500 : 650,
              filterFrequencyEnd: isInspi ? 950 : 320,
              attackRatio: 0.35,
              releaseRatio: 0.4,
            });
            break;
          }
        }
      } catch (error) {
        console.warn("Erreur guide audio :", error);
      }
    },
    stopActiveAudio() {
      if (!this.audioCtx) return;
      const now = this.audioCtx.currentTime;
      this.activeAudioNodes.forEach((nodeGroup) => {
        try {
          nodeGroup.gain.gain.cancelScheduledValues(now);
          nodeGroup.gain.gain.setValueAtTime(
            Math.max(nodeGroup.gain.gain.value, 0.0001),
            now,
          );
          nodeGroup.gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.05);
          nodeGroup.oscillator.stop(now + 0.06);
        } catch (e) {
          // ignore
        }
      });
      this.activeAudioNodes = [];
    },
    disconnectAudioNodeGroup(nodeGroup) {
      try {
        if (nodeGroup.source) nodeGroup.source.disconnect();
        if (nodeGroup.oscillator) nodeGroup.oscillator.disconnect();
        if (nodeGroup.filter) nodeGroup.filter.disconnect();
        if (nodeGroup.gain) nodeGroup.gain.disconnect();
      } catch (e) {
        // ignore
      }
    },
    recupDataCPM() {
      const Data = Parse.Object.extend("user_param_game");
      const query = new Parse.Query(Data);
      const User = Parse.Object.extend("User");
      const queryUser = new Parse.Query(User);
      queryUser.equalTo("objectId", Parse.User.current().id);
      query.matchesQuery("user", queryUser);
      query.first().then((result) => {
        if (!result) return;
        const data = JSON.parse(result.attributes.paramJeux);
        this.inspiration = data.Inspi;
        this.expiration = data.Expi;
        this.pauseInspiration = data.InspiPause;
        this.pauseExpiration = data.ExpiPause;
        this.restartBreathingSystem();
      });
    },
    async SaveDataCPM() {
      this.openModal = false;
      const data_cpm = JSON.stringify({
        Inspi: this.inspiration,
        Expi: this.expiration,
        InspiPause: this.pauseInspiration,
        ExpiPause: this.pauseExpiration,
      });
      const user = Parse.User.current();
      const Data = Parse.Object.extend("user_param_game");
      const DataUser = Parse.Object.extend("User");
      const queryUser = new Parse.Query(DataUser);
      const query = new Parse.Query(Data);
      if (this.idUserForSave) {
        queryUser.equalTo("objectId", this.idUserForSave);
        const user_cpm = await queryUser.find();
        this.UtilisateurCPM = user_cpm[0];
        query.equalTo("user", this.UtilisateurCPM);
      } else if (this.idPatientTherapeute) {
        queryUser.equalTo("objectId", this.idPatientTherapeute);
        const user_cpm = await queryUser.find();
        this.UtilisateurCPM = user_cpm[0];
        query.equalTo("user", this.UtilisateurCPM);
      } else {
        query.equalTo("user", user);
        this.UtilisateurCPM = user;
      }
      query
        .first()
        .then((result) => {
          if (result) {
            result.set("paramJeux", data_cpm);
            return result.save();
          }
          const dataDone = new Data();
          return dataDone.save({
            user: this.UtilisateurCPM,
            paramJeux: data_cpm,
          });
        })
        .then(
          () => {
            console.log("Data saved successfully");
            this.restartBreathingSystem();
          },
          (error) => {
            console.error("Error while saving Data: ", error);
          },
        );
    },
  },
  mounted() {
    this.updateBreathingPattern();
    this.updateAnimation();
    this.emitBreathSettings();
    this.restartAudioCycle();
    this.$watch("openModal", (v) => {
      this.syncPortalInteraction();
      if (v) this.$nextTick(() => this.ensurePortalMount());
    });
  },
  beforeDestroy() {
    this.cycleToken += 1;
    this.stopCycleTimer();
    this.stopPhaseCountdown();
    this.stopActiveAudio();
    if (this.audioCtx) {
      this.audioCtx.close();
      this.audioCtx = null;
    }
    const style = document.getElementById("breathing-animation-style");
    if (style) {
      style.remove();
    }
  },
};
</script>
<style scoped>
/* =========================================================
   GUIDE RESPIRATOIRE COMPACT
   ========================================================= */

.breathing-guide {
  display: flex;
  align-items: center;
  justify-content: center;

  width: auto;
  height: auto;

  min-width: 0;
  min-height: 0;
}

.breathing-guide--disabled {
  opacity: 0.6;
  pointer-events: none;
}

.breathing-guide__body {
  display: flex;
  align-items: center;
  justify-content: center;

  width: auto;
  height: auto;

  min-width: 0;
  min-height: 0;
}

.breathing-bar {
  position: relative;

  display: flex;
  align-items: center;
  justify-content: center;

  width: auto;
  height: auto;

  padding: 0;
}

.breathing-visual {
  display: flex;
  align-items: center;
  justify-content: center;

  width: auto;
  height: auto;

  gap: 0;
}


/* =========================================================
   ZONE DU CERCLE
   ========================================================= */

.breathing-circle-container {
  position: relative;

  width: 92px;
  height: 92px;

  display: flex;
  align-items: center;
  justify-content: center;

  flex: 0 0 92px;
}


/* =========================================================
   ANNEAU DE PROGRESSION
   ========================================================= */

.breathing-progress {
  position: absolute;

  left: 50%;
  top: 50%;

  width: 92px;
  height: 92px;

  transform:
    translate(-50%, -50%) rotate(-90deg);

  z-index: 3;

  pointer-events: none;

  overflow: visible;
}

.breathing-progress circle {
  fill: none;
  stroke-width: 3;
}

.breathing-progress__background {
  stroke: rgba(190, 225, 223, 0.32);
}

.breathing-progress__value {
  stroke: rgba(123, 190, 187, 0.95);

  stroke-linecap: round;

  transition: none;
}


/* =========================================================
   CERCLE RESPIRATOIRE
   ========================================================= */

.breathing-circle {
  position: absolute;

  left: 50%;
  top: 50%;

  width: 74px;
  height: 74px;

  margin-left: -37px;
  margin-top: -37px;

  border-radius: 50%;
  border: none;

  overflow: hidden;

  transform-origin: center;

  z-index: 2;

  background: rgba(167, 237, 234, 0.06);

  /*
   * Halo beaucoup moins massif que précédemment.
   * Le dégradé dynamique reste fourni par gaugeStyle.
   */
  box-shadow:
    0 0 14px rgba(75, 220, 214, 0.14);
}

.breathing-circle__fill-track {
  position: absolute;
  inset: 0;

  border-radius: 50%;
  overflow: hidden;

  background:
    radial-gradient(circle at center,
      rgba(90, 230, 224, 0.08) 0%,
      rgba(90, 230, 224, 0.05) 55%,
      rgba(90, 230, 224, 0.02) 100%);
}

.breathing-circle__fill {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;

  height: 0%;

  background:
    linear-gradient(to top,
      rgba(45, 245, 236, 0.88) 0%,
      rgba(78, 239, 233, 0.70) 38%,
      rgba(121, 236, 232, 0.38) 72%,
      rgba(165, 239, 236, 0.12) 100%);

  transition: none;
}


/* =========================================================
   BOUTON AUDIO CENTRAL
   ========================================================= */

.breathing-circle__sound {
  position: absolute;

  left: 50%;
  top: 50%;

  transform: translate(-50%, -50%);

  width: 24px;
  height: 24px;

  display: flex;
  align-items: center;
  justify-content: center;

  padding: 0;

  border: 1px solid rgba(255, 255, 255, 0.28);
  border-radius: 50%;

  background: rgba(255, 255, 255, 0.36);

  color: rgba(65, 110, 108, 0.78);

  font-size: 10px;

  cursor: pointer;

  z-index: 4;

  backdrop-filter: blur(4px);
  -webkit-backdrop-filter: blur(4px);

  transition:
    background 0.2s ease,
    color 0.2s ease,
    transform 0.15s ease;
}

.breathing-circle__sound:hover {
  background: rgba(255, 255, 255, 0.62);
  color: #536f6e;
}

.breathing-circle__sound--active {
  background: rgba(255, 255, 255, 0.70);
  color: #536f6e;
}

.breathing-circle__sound:active {
  transform: translate(-50%, -50%) scale(0.92);
}

.breathing-settings-btn {
  align-self: center;
  border: 1px solid #e5e7eb;
  background: #f9fafb;
  color: #374151;
  font-weight: 600;
  padding: 6px 12px;
  border-radius: 999px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.breathing-settings-btn:hover {
  background: #f3f4f6;
  border-color: #d1d5db;
}

.icon-btn {
  border: none;
  background: none;
  color: #9ca3af;
  width: 28px;
  height: 28px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 0.875rem;
}

.icon-btn:hover {
  color: #3b82f6;
  background: rgba(59, 130, 246, 0.08);
}

.icon-btn--small {
  width: 24px;
  height: 24px;
  font-size: 0.75rem;
}

.breathing-modal__overlay {
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.5);
  backdrop-filter: blur(8px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 12001;
  padding: 20px;
}

.breathing-modal {
  background: #fff;
  border-radius: 12px;
  width: min(560px, 90vw);
  max-height: 85vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 20px 50px rgba(15, 23, 42, 0.15);
}

.breathing-modal__header {
  padding: 16px 20px;
  border-bottom: 1px solid #e5e7eb;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
}

.breathing-modal__title {
  margin: 0;
  font-size: 1rem;
  font-weight: 700;
  color: #1f2937;
}

.breathing-modal__subtitle {
  margin: 4px 0 0;
  font-size: 0.8125rem;
  color: #6b7280;
}

.breathing-modal__body {
  padding: 16px 20px;
  display: grid;
  gap: 14px;
  overflow-y: auto;
}

.breathing-modal__section {
  display: grid;
  gap: 8px;
}

.field-label {
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  color: #6b7280;
  font-weight: 600;
}

.field-input {
  height: 36px;
  border-radius: 6px;
  border: 1px solid #e5e7eb;
  padding: 0 10px;
  font-size: 0.875rem;
  color: #1f2937;
  background: #fff;
  font-family: inherit;
}

.timing-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;
}

.timing-card {
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 10px;
  display: grid;
  gap: 8px;
  font-size: 0.75rem;
  color: #6b7280;
}

.timing-controls {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 6px;
  color: #1f2937;
  font-weight: 600;
}

.checkbox-row {
  display: flex;
  gap: 8px;
  align-items: center;
  font-size: 0.8125rem;
  color: #1f2937;
}

.breathing-modal__footer {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  padding: 12px 20px;
  border-top: 1px solid #e5e7eb;
}

.ghost-btn {
  border: 1px solid #e5e7eb;
  background: transparent;
  color: #1f2937;
  padding: 8px 12px;
  border-radius: 6px;
  font-weight: 600;
  cursor: pointer;
  font-size: 0.875rem;
  transition: all 0.2s ease;
}

.ghost-btn:hover {
  border-color: #3b82f6;
  color: #3b82f6;
}

.primary-btn {
  border: none;
  background: #3b82f6;
  color: #fff;
  padding: 8px 12px;
  border-radius: 6px;
  font-weight: 600;
  cursor: pointer;
  font-size: 0.875rem;
  transition: all 0.2s ease;
}

.primary-btn:hover {
  background: #1e40af;
}

@media (max-width: 768px) {
  .breathing-guide__body {
    width: auto;
    height: auto;
  }

  .breathing-bar {
    width: auto;
    height: auto;
  }

  .breathing-circle-container {
    width: 92px;
    height: 92px;
    flex-basis: 92px;
  }

  .timing-grid {
    grid-template-columns: 1fr;
  }

  .breathing-modal {
    width: 95vw;
  }
}
</style>